import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { MessageCircle } from "lucide-react";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import ProgramsPage from "@/pages/ProgramsPage";
import EnrollmentPage from "@/pages/EnrollmentPage";
import ThankYouPage from "@/pages/ThankYouPage";
import MessagesPage from "@/pages/MessagesPage";
import PuppyProfilePage from "@/pages/PuppyProfilePage";
import FAQPage from "@/pages/FAQPage";
import TrainerDashboard from "@/pages/TrainerDashboard";
import CompletePaymentPage from "@/pages/CompletePaymentPage";
import InvoicePaymentPage from "@/pages/InvoicePaymentPage";

// Floating Messenger Button - appears on all pages
const FloatingMessenger = () => (
  <a
    href="https://m.me/ThePackStandard"
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-20 right-6 z-50 bg-olive hover:bg-olive-dark text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center gap-2 group"
    data-testid="floating-messenger-btn"
    aria-label="Ask a Question on Facebook"
  >
    <MessageCircle className="w-6 h-6" />
    <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap font-medium">
      Ask a Question
    </span>
  </a>
);

// Protected Route Component
const ProtectedRoute = ({ children, trainerOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="animate-pulse text-olive font-heading text-2xl">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (trainerOnly && user.role !== "trainer") {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App font-body">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/programs" element={<ProgramsPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/complete-payment/:enrollmentId" element={<CompletePaymentPage />} />
            <Route path="/pay-invoice/:invoiceId" element={<InvoicePaymentPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enroll/:intakeId"
              element={
                <ProtectedRoute>
                  <EnrollmentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/thank-you"
              element={
                <ProtectedRoute>
                  <ThankYouPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/puppies/:puppyId"
              element={
                <ProtectedRoute>
                  <PuppyProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trainer"
              element={
                <ProtectedRoute trainerOnly>
                  <TrainerDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <FloatingMessenger />
      </div>
    </AuthProvider>
  );
}

export default App;
